import { useState } from 'react';
import { Clock, X } from 'lucide-react';
import type { AvailabilityRule } from '../hooks/useAvailability';

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface StandardWeekEditorProps {
  availability: AvailabilityRule[];
  onSaveDay: (dayOfWeek: number, startTime: string, endTime: string) => void;
  onDeleteDay: (dayOfWeek: number) => void;
  onSetStandardHours: () => void;
  isSaving: boolean;
}

export function StandardWeekEditor({
  availability,
  onSaveDay,
  onDeleteDay,
  onSetStandardHours,
  isSaving,
}: StandardWeekEditorProps) {
  const [editingDay, setEditingDay] = useState<number | null>(null);
  const [form, setForm] = useState({ startTime: '08:00', endTime: '17:00' });

  const getRuleForDay = (dayOfWeek: number): AvailabilityRule | null => {
    return availability.find((rule) => rule.dayOfWeek === dayOfWeek) ?? null;
  };

  const handleTapDay = (dayOfWeek: number) => {
    if (editingDay === dayOfWeek) {
      setEditingDay(null);
      return;
    }
    const rule = getRuleForDay(dayOfWeek);
    setForm({
      startTime: rule?.startTime ?? '08:00',
      endTime: rule?.endTime ?? '17:00',
    });
    setEditingDay(dayOfWeek);
  };

  const handleSave = () => {
    if (editingDay === null) return;
    onSaveDay(editingDay, form.startTime, form.endTime);
    setEditingDay(null);
  };

  const handleSetOff = () => {
    if (editingDay === null) return;
    onDeleteDay(editingDay);
    setEditingDay(null);
  };

  return (
    <div className="bg-gray-50 rounded-lg border border-gray-200 p-3">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-900">Standard Week</span>
        </div>
        <button
          onClick={onSetStandardHours}
          disabled={isSaving}
          className="text-xs text-blue-600 font-medium disabled:opacity-50"
        >
          Set M-F 8-5
        </button>
      </div>

      {/* 7-day grid */}
      <div className="grid grid-cols-7 gap-1">
        {DAYS_OF_WEEK.map((dayName, dayIndex) => {
          const rule = getRuleForDay(dayIndex);
          const hasHours = rule !== null;
          const isEditing = editingDay === dayIndex;

          return (
            <button
              type="button"
              key={dayIndex}
              onClick={() => handleTapDay(dayIndex)}
              className={`
                flex flex-col items-center p-2 rounded-md text-xs transition-colors min-h-[48px]
                ${isEditing
                  ? 'bg-blue-100 border-2 border-blue-500'
                  : hasHours
                    ? 'bg-green-50 border border-green-200'
                    : 'bg-white border border-gray-200'
                }
              `}
            >
              <span className={`font-medium ${hasHours ? 'text-green-800' : 'text-gray-500'}`}>
                {dayName}
              </span>
              <span className={`mt-0.5 ${hasHours ? 'text-green-700' : 'text-gray-400'}`}>
                {hasHours ? `${rule.startTime.substring(0, 5)}` : 'Off'}
              </span>
              {hasHours && (
                <span className="text-green-700">
                  {rule.endTime.substring(0, 5)}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Inline edit form */}
      {editingDay !== null && (
        <div className="mt-3 p-3 bg-white rounded-md border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-900">
              Edit {DAYS_OF_WEEK[editingDay]} Hours
            </span>
            <button
              onClick={() => setEditingDay(null)}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <X className="h-4 w-4 text-gray-500" />
            </button>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Start</label>
              <input
                type="time"
                value={form.startTime}
                onChange={(e) => setForm((prev) => ({ ...prev, startTime: e.target.value }))}
                className="px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white text-gray-900"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">End</label>
              <input
                type="time"
                value={form.endTime}
                onChange={(e) => setForm((prev) => ({ ...prev, endTime: e.target.value }))}
                className="px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white text-gray-900"
              />
            </div>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50"
            >
              Save
            </button>
            {getRuleForDay(editingDay) && (
              <button
                onClick={handleSetOff}
                disabled={isSaving}
                className="px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-md border border-red-200 transition-colors disabled:opacity-50"
              >
                Set Off
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
